import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { GOOGLE_CLIENT_ID } from "../lib/config";
import { AppError } from "../lib/errors";
import { getGoogleAccountsId, loadGoogleIdentityServices } from "../lib/googleGis";
import { storage } from "../lib/storage";

// ---- 型 ----

export type LineAccountLinkState = {
  lineCompletionTarget?: "home" | "lineComplete";
  nextPath?: string;
};

type Method = "select" | "email" | "emailVerify" | "google" | "done";

// ---- エラーコード解釈 ----

function resolveConflictMessage(errorCode: string | undefined): string | null {
  if (errorCode === "email_belongs_to_other_account") {
    return "このメールアドレスはすでに別のWizMateアカウントで使用されています。サポートへお問い合わせください。";
  }
  if (errorCode === "current_account_has_different_email") {
    return "このアカウントにはすでに別のメールアドレスが登録されています。サポートへお問い合わせください。";
  }
  return null;
}

function parseOtpReason(responseText: string | undefined): "invalid" | "expired" | null {
  if (!responseText) return null;
  try {
    const parsed = JSON.parse(responseText) as { reason?: unknown; data?: { reason?: unknown } };
    const r = parsed?.reason ?? parsed?.data?.reason;
    if (r === "invalid" || r === "expired") return r;
  } catch { /* ignore */ }
  return null;
}

// ---- コンポーネント ----

export default function LineAccountLinkPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (
    typeof location.state === "object" && location.state ? location.state : {}
  ) as LineAccountLinkState;

  const nextPath = routeState.nextPath ?? "/home";
  const lineCompletionTarget = routeState.lineCompletionTarget;

  const [method, setMethod] = useState<Method>("select");

  // メール OTP フロー
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // OTP コード検証フロー
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Google リンクフロー
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleLinking, setGoogleLinking] = useState(false);

  const submittedRef = useRef(false);

  const handleDone = useCallback(() => {
    if (lineCompletionTarget === "lineComplete") {
      navigate("/line-register/complete", { replace: true });
    } else {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, lineCompletionTarget, nextPath]);

  // ---- メール OTP 送信 ----

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || emailLoading) return;

    setEmailLoading(true);
    setEmailError(null);

    try {
      await api.requestAccountLinkEmailOtp(trimmed);
      setMethod("emailVerify");
    } catch (err) {
      if (err instanceof AppError) {
        if (err.meta.status === 429) {
          setEmailError("しばらく時間をおいてからお試しください。");
        } else if (err.meta.status === 422 || err.code === "BAD_REQUEST") {
          setEmailError("メールアドレスの形式が正しくありません。");
        } else {
          setEmailError("送信に失敗しました。時間をおいて再度お試しください。");
        }
      } else {
        setEmailError("送信に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // ---- OTP コード検証 ----

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (trimmedCode.length !== 6 || codeLoading || submittedRef.current) return;

    setCodeLoading(true);
    setCodeError(null);
    submittedRef.current = true;

    try {
      const result = await api.verifyAccountLinkEmailOtp(email.trim(), trimmedCode);
      const conflict = resolveConflictMessage(result.errorCode);
      if (conflict) {
        setCodeError(conflict);
        submittedRef.current = false;
        return;
      }
      setMethod("done");
      handleDone();
    } catch (err) {
      submittedRef.current = false;
      if (err instanceof AppError) {
        if (err.meta.status === 409) {
          try {
            const parsed = JSON.parse(err.meta.responseText ?? "{}") as { data?: { errorCode?: string } };
            const conflict2 = resolveConflictMessage(parsed?.data?.errorCode);
            setCodeError(conflict2 ?? "このメールアドレスは別のアカウントで使用されています。");
          } catch {
            setCodeError("このメールアドレスは別のアカウントで使用されています。");
          }
        } else if (err.meta.status === 429) {
          setCodeError("しばらく時間をおいてからお試しください。");
        } else if (err.code === "BAD_REQUEST") {
          const reason = parseOtpReason(err.meta.responseText);
          setCodeError(reason === "expired" ? "有効期限が切れています。コードを再送してください。" : "確認コードが正しくありません。");
        } else {
          setCodeError("確認に失敗しました。時間をおいて再度お試しください。");
        }
      } else {
        setCodeError("確認に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setResendMessage(null);
    try {
      await api.requestAccountLinkEmailOtp(email.trim());
      setResendMessage("確認コードを再送しました。メールをご確認ください。");
    } catch {
      setResendMessage("再送に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setResending(false);
    }
  };

  // ---- Google リンク ----

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      if (googleLinking) return;
      setGoogleLinking(true);
      setGoogleError(null);
      try {
        const result = await api.linkAccountGoogle(credential);
        const conflict = resolveConflictMessage(result.errorCode);
        if (conflict) {
          setGoogleError(conflict);
          return;
        }
        setMethod("done");
        handleDone();
      } catch (err) {
        if (err instanceof AppError && err.meta.status === 409) {
          try {
            const parsed = JSON.parse(err.meta.responseText ?? "{}") as { data?: { errorCode?: string } };
            setGoogleError(resolveConflictMessage(parsed?.data?.errorCode) ?? "このGoogleアカウントは別のアカウントで使用されています。");
          } catch {
            setGoogleError("このGoogleアカウントは別のアカウントで使用されています。");
          }
        } else {
          setGoogleError("Google連携に失敗しました。もう一度お試しください。");
        }
      } finally {
        setGoogleLinking(false);
      }
    },
    [googleLinking, handleDone]
  );

  useEffect(() => {
    if (method !== "google" || !GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    let disposed = false;

    const render = async () => {
      try {
        await loadGoogleIdentityServices();
        if (disposed || !googleBtnRef.current) return;
        const gid = getGoogleAccountsId();
        if (!gid) return;
        googleBtnRef.current.innerHTML = "";
        gid.initialize({ client_id: GOOGLE_CLIENT_ID, callback: ({ credential }: { credential: string }) => { void handleGoogleCredential(credential); } });
        gid.renderButton(googleBtnRef.current, { theme: "outline", size: "large", width: "248", locale: "ja" });
      } catch {
        if (!disposed) setGoogleError("Googleの読み込みに失敗しました。ページを読み直してください。");
      }
    };

    void render();
    return () => { disposed = true; };
  }, [method, handleGoogleCredential]);

  const auth = storage.getAuth();
  if (!auth.userId && !auth.accessToken && !auth.refreshToken) {
    return <Navigate to="/splash" replace />;
  }

  // ---- 画面共通フレーム ----

  const Card = ({ children }: { children: React.ReactNode }) => (
    <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <div className="w-full max-w-sm">
        <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-[#F4E3D7] opacity-70 blur-3xl" />
          <div className="px-8 pb-10 pt-12">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#A67C52]">PERSONAL CONCIERGE</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>
            {children}
          </div>
          <div className="border-t border-[#EADBC8] px-6 py-4 text-center">
            <p className="text-[11px] text-[#9C867D]">© WizMate</p>
          </div>
        </div>
      </div>
    </main>
  );

  // ---- 画面: 方法選択 ----

  if (method === "select") {
    return (
      <Card>
        <div className="mt-6">
          <p className="text-base font-black text-[#5D4D46]">メールアドレスまたはGoogleを登録してください</p>
          <p className="mt-2 text-sm leading-6 text-[#7B6861]">
            WebブラウザからログインするためのメールアドレスまたはGoogleアカウントの登録が必要です。
          </p>
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => setMethod("email")}
              className="w-full rounded-2xl bg-[#5D4D46] px-4 py-4 text-sm font-bold text-white shadow-[0_4px_12px_rgba(93,77,70,0.2)] transition hover:bg-[#4A3E39] active:scale-[0.99]"
            >
              メールアドレスで登録
            </button>
            {GOOGLE_CLIENT_ID ? (
              <button
                type="button"
                onClick={() => setMethod("google")}
                className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-4 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5] active:scale-[0.99]"
              >
                Googleで登録
              </button>
            ) : null}
          </div>
        </div>
      </Card>
    );
  }

  // ---- 画面: メールアドレス入力 ----

  if (method === "email") {
    return (
      <Card>
        <div className="mt-6">
          <p className="text-base font-black text-[#5D4D46]">メールアドレスを入力してください</p>
          {emailError ? (
            <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">
              {emailError}
            </p>
          ) : null}
          <form className="mt-4" onSubmit={(e) => void handleEmailSubmit(e)}>
            <label htmlFor="account-link-email" className="text-sm font-black text-[#5D4D46]">
              メールアドレス
            </label>
            <input
              id="account-link-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
              className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
              placeholder="example@example.com"
              autoFocus
              disabled={emailLoading}
              autoComplete="email"
            />
            <button
              type="submit"
              disabled={!email.trim() || emailLoading}
              className="mt-5 w-full rounded-2xl bg-[#5D4D46] px-4 py-4 text-sm font-bold text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {emailLoading ? "送信中..." : "確認コードを送る"}
            </button>
          </form>
          <button
            type="button"
            onClick={() => setMethod("select")}
            className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]"
          >
            戻る
          </button>
        </div>
      </Card>
    );
  }

  // ---- 画面: OTP コード入力 ----

  if (method === "emailVerify") {
    return (
      <Card>
        <div className="mt-6">
          <p className="text-base font-black text-[#5D4D46]">確認コードを入力してください</p>
          <p className="mt-2 text-sm leading-6 text-[#7B6861]">
            {email.trim()} に確認コードを送信しました。
          </p>
          {codeError ? (
            <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">
              {codeError}
            </p>
          ) : null}
          <form className="mt-4" onSubmit={(e) => void handleCodeSubmit(e)}>
            <label htmlFor="account-link-code" className="text-sm font-black text-[#5D4D46]">
              確認コード（6桁）
            </label>
            <input
              id="account-link-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setCodeError(null); }}
              className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-center text-lg font-black tracking-[0.4em] text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
              placeholder="000000"
              autoFocus
              disabled={codeLoading}
              autoComplete="one-time-code"
            />
            <button
              type="submit"
              disabled={code.trim().length !== 6 || codeLoading}
              className="mt-5 w-full rounded-2xl bg-[#5D4D46] px-4 py-4 text-sm font-bold text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {codeLoading ? "確認中..." : "確認する"}
            </button>
          </form>
          <div className="mt-4 text-center">
            {resendMessage ? (
              <p className="text-xs font-medium text-[#7B6861]" aria-live="polite">{resendMessage}</p>
            ) : (
              <button
                type="button"
                onClick={() => void handleResend()}
                disabled={resending}
                className="text-sm font-bold text-[#A67C52] underline underline-offset-2 transition hover:text-[#7C5A4E] disabled:opacity-60"
              >
                {resending ? "再送中..." : "コードを再送する"}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setCode(""); setCodeError(null); submittedRef.current = false; setMethod("email"); }}
            className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]"
          >
            メールアドレスを変更する
          </button>
        </div>
      </Card>
    );
  }

  // ---- 画面: Google 連携 ----

  if (method === "google") {
    return (
      <Card>
        <div className="mt-6">
          <p className="text-base font-black text-[#5D4D46]">Googleアカウントで登録</p>
          <p className="mt-2 text-sm leading-6 text-[#7B6861]">
            Webブラウザからのログインに使用するGoogleアカウントを選択してください。
          </p>
          {googleError ? (
            <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">
              {googleError}
            </p>
          ) : null}
          {googleLinking ? (
            <div className="mt-6 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4 text-center">
              <p className="text-sm font-bold text-[#5D4D46]">連携しています...</p>
              <div className="mt-3 flex items-center justify-center gap-1.5">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
              </div>
            </div>
          ) : (
            <div className="mt-6 flex justify-center">
              <div ref={googleBtnRef} />
            </div>
          )}
          <button
            type="button"
            onClick={() => { setGoogleError(null); setMethod("select"); }}
            className="mt-4 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]"
          >
            戻る
          </button>
        </div>
      </Card>
    );
  }

  return null;
}
